import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, Block as BlockIcon, RestoreFromTrash as RestoreIcon, DeleteForever as DeleteIcon } from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import { api } from '../services/api';

type CreditStatusFilter = 'active' | 'completed' | 'defaulted';

interface Credit {
  id: number;
  customer_name: string;
  total_amount: number;
  installments: number;
  installment_amount: number;
  total_paid: number;
  is_active: boolean;
  status?: CreditStatusFilter;
  writeoff_reason?: string | null;
  start_date: string;
  interest_rate: number;
  vendor_name?: string | null;
  collector_name?: string | null;
}

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
}

interface Vendor {
  id: number;
  name: string;
}

interface AppUser {
  id: number;
  username: string;
  is_active: boolean;
  role: string;
}

export default function CreditosPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [credits, setCredits] = useState<Credit[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<CreditStatusFilter>('active');
  const [writeoffDialog, setWriteoffDialog] = useState<{ open: boolean; credit: Credit | null; reason: string }>({
    open: false,
    credit: null,
    reason: '',
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; credit: Credit | null; loading: boolean; error: string }>({
    open: false,
    credit: null,
    loading: false,
    error: '',
  });
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    customer_id: 0,
    amount: '',
    interest_rate: '0',
    installments: '',
    collection_day: '1',
    vendor_name: '',
    collector_name: '',
  });

  useEffect(() => {
    fetchCredits();
    fetchCustomers();
    fetchVendors();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchVendors = async () => {
    try {
      const res = await api.get('/vendors');
      setVendors(res.data || []);
    } catch {
      setVendors([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setAppUsers(res.data || []);
    } catch {
      setAppUsers([]);
    }
  };

  const fetchCredits = async () => {
    setLoading(true);
    try {
      const res = await api.get('/credits', { params: { status: statusFilter } });
      setCredits(res.data || []);
    } catch (e) {
      setCredits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDefaulted = async () => {
    if (!writeoffDialog.credit) return;
    try {
      await api.put(`/credits/${writeoffDialog.credit.id}/status`, {
        status: 'defaulted',
        reason: writeoffDialog.reason || null,
      });
      setSuccess('Crédito marcado como incobrable');
      setWriteoffDialog({ open: false, credit: null, reason: '' });
      fetchCredits();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleReactivate = async (credit: Credit) => {
    try {
      await api.put(`/credits/${credit.id}/status`, { status: 'active' });
      setSuccess('Crédito reactivado');
      fetchCredits();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.credit) return;
    setDeleteDialog((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      await api.delete(`/sales/${deleteDialog.credit.id}`);
      setSuccess('Crédito eliminado (junto con el gasto y los pagos asociados)');
      setDeleteDialog({ open: false, credit: null, loading: false, error: '' });
      fetchCredits();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setDeleteDialog((prev) => ({
        ...prev,
        loading: false,
        error: e.response?.data?.detail || 'Error al eliminar el crédito',
      }));
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(res.data || []);
    } catch (e) {
      setCustomers([]);
    }
  };

  const handleCreate = async () => {
    setError('');
    const amount = parseFloat(form.amount);
    const interestRate = parseFloat(form.interest_rate);
    const installments = parseInt(form.installments);
    const collectionDay = parseInt(form.collection_day);

    if (!form.customer_id || !amount || !installments) {
      setError('Complete todos los campos obligatorios');
      return;
    }

    const totalAmount = amount * (1 + interestRate / 100);
    const installmentAmount = totalAmount / installments;

    try {
      await api.post('/credits', {
        customer_id: form.customer_id,
        amount,
        interest_rate: interestRate,
        installments,
        installment_amount: Math.round(installmentAmount * 100) / 100,
        total_amount: Math.round(totalAmount * 100) / 100,
        collection_day: collectionDay,
        vendor_name: form.vendor_name || undefined,
        collector_name: form.collector_name || undefined,
      });
      setSuccess('Crédito creado exitosamente');
      setDialogOpen(false);
      setForm({ customer_id: 0, amount: '', interest_rate: '0', installments: '', collection_day: '1', vendor_name: '', collector_name: '' });
      fetchCredits();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Error al crear crédito');
    }
  };

  const totalAmount = parseFloat(form.amount || '0') * (1 + parseFloat(form.interest_rate || '0') / 100);
  const installmentPreview = form.installments ? totalAmount / parseInt(form.installments || '1') : 0;

  const filteredCredits = useMemo(() => {
    if (!search.trim()) return credits;
    const term = search.toLowerCase();
    return credits.filter((c) => c.customer_name.toLowerCase().includes(term));
  }, [credits, search]);

  const totals = useMemo(() => {
    const data = filteredCredits;
    return {
      total: data.reduce((s, c) => s + c.total_amount, 0),
      paid: data.reduce((s, c) => s + c.total_paid, 0),
      pending: data.reduce((s, c) => s + Math.max(0, c.total_amount - c.total_paid), 0),
      count: data.length,
    };
  }, [filteredCredits]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Créditos / Préstamos</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Nuevo Crédito
        </Button>
      </Box>

      {/* Totales */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Créditos', value: totals.count, color: '#6366F1', isCurrency: false },
          { label: 'Monto Total', value: totals.total, color: '#3B82F6', isCurrency: true },
          { label: 'Cobrado', value: totals.paid, color: '#10B981', isCurrency: true },
          { label: 'Pendiente', value: totals.pending, color: '#EF4444', isCurrency: true },
        ].map((card) => (
          <Grid item xs={6} sm={3} key={card.label}>
            <Paper sx={{ p: 2, borderTop: `3px solid ${card.color}` }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                {card.label}
              </Typography>
              <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: card.color }}>
                {card.isCurrency ? `$${card.value.toLocaleString()}` : card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filtro de estado */}
      <Tabs
        value={statusFilter}
        onChange={(_, v) => setStatusFilter(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Activos" value="active" />
        <Tab label="Completados" value="completed" />
        <Tab label="Incobrables" value="defaulted" />
      </Tabs>

      {/* Buscador */}
      <TextField
        fullWidth
        size="small"
        placeholder="Buscar por nombre de cliente..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {isMobile ? (
        // Vista mobile: cards apilados (mucho mejor que tabla 10 col)
        <Stack spacing={1.5}>
          {filteredCredits.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {search ? 'Sin resultados' : 'No hay créditos registrados'}
              </Typography>
            </Paper>
          ) : (
            filteredCredits.map((credit) => {
              const pending = credit.total_amount - credit.total_paid;
              const paidInstallments = Math.floor(credit.total_paid / credit.installment_amount);
              const progress = credit.total_amount > 0 ? Math.min(100, (credit.total_paid / credit.total_amount) * 100) : 0;
              const status = credit.status || (credit.is_active ? 'active' : 'completed');
              const statusLabel = status === 'active' ? 'Activo' : status === 'completed' ? 'Completado' : 'Incobrable';
              const statusColor: 'primary' | 'success' | 'error' = status === 'active' ? 'primary' : status === 'completed' ? 'success' : 'error';
              return (
                <Card key={credit.id} variant="outlined">
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    {/* Header: cliente + estado */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }} noWrap>
                          {credit.customer_name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', textTransform: 'capitalize' }}>
                          {credit.vendor_name || 'Sin vendedor'} · {credit.collector_name || 'Sin cobrador'}
                        </Typography>
                      </Box>
                      <Tooltip title={credit.writeoff_reason || ''} disableHoverListener={!credit.writeoff_reason}>
                        <Chip label={statusLabel} color={statusColor} size="small" />
                      </Tooltip>
                    </Box>

                    {/* Progreso de cuotas */}
                    <Box sx={{ mb: 1.25 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          Cuota {paidInstallments}/{credit.installments}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          ${credit.installment_amount.toLocaleString()} c/u
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 6, borderRadius: 3 }}
                        color={status === 'defaulted' ? 'error' : 'primary'}
                      />
                    </Box>

                    {/* Métricas */}
                    <Grid container spacing={1} sx={{ mb: 1.25 }}>
                      <Grid item xs={4}>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase' }}>Total</Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          ${credit.total_amount.toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase' }}>Pagado</Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'success.main' }}>
                          ${credit.total_paid.toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase' }}>Pendiente</Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: pending > 0 ? 'error.main' : 'success.main' }}>
                          ${Math.max(0, pending).toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Acciones */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      {status === 'active' && (
                        <Tooltip title="Marcar como incobrable">
                          <IconButton color="warning" onClick={() => setWriteoffDialog({ open: true, credit, reason: '' })} sx={{ width: 44, height: 44 }}>
                            <BlockIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {status === 'defaulted' && (
                        <Tooltip title="Reactivar crédito">
                          <IconButton color="primary" onClick={() => handleReactivate(credit)} sx={{ width: 44, height: 44 }}>
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Eliminar crédito (definitivo)">
                        <IconButton color="error" onClick={() => setDeleteDialog({ open: true, credit, loading: false, error: '' })} sx={{ width: 44, height: 44 }}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Vendedor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Cobrador</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Monto Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Cuotas</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Cuota</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Pagado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Pendiente</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Estado</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCredits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">{search ? 'Sin resultados' : 'No hay créditos registrados'}</TableCell>
                </TableRow>
              ) : filteredCredits.map((credit) => {
                const pending = credit.total_amount - credit.total_paid;
                const paidInstallments = Math.floor(credit.total_paid / credit.installment_amount);
                const status = credit.status || (credit.is_active ? 'active' : 'completed');
                const statusLabel = status === 'active' ? 'Activo' : status === 'completed' ? 'Completado' : 'Incobrable';
                const statusColor: 'primary' | 'success' | 'error' = status === 'active' ? 'primary' : status === 'completed' ? 'success' : 'error';
                return (
                  <TableRow key={credit.id}>
                    <TableCell>{credit.customer_name}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{credit.vendor_name || '—'}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{credit.collector_name || '—'}</TableCell>
                    <TableCell align="right">${credit.total_amount.toLocaleString()}</TableCell>
                    <TableCell align="right">{paidInstallments}/{credit.installments}</TableCell>
                    <TableCell align="right">${credit.installment_amount.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>${credit.total_paid.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: pending > 0 ? 'error.main' : 'success.main' }}>
                      ${Math.max(0, pending).toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={credit.writeoff_reason || ''} disableHoverListener={!credit.writeoff_reason}>
                        <Chip label={statusLabel} color={statusColor} size="small" />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {status === 'active' && (
                        <Tooltip title="Marcar como incobrable">
                          <IconButton size="small" color="warning" onClick={() => setWriteoffDialog({ open: true, credit, reason: '' })}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {status === 'defaulted' && (
                        <Tooltip title="Reactivar crédito">
                          <IconButton size="small" color="primary" onClick={() => handleReactivate(credit)}>
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Eliminar crédito (definitivo)">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, credit, loading: false, error: '' })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Nuevo Crédito</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={customers}
                getOptionLabel={(c) => `${c.last_name} ${c.first_name}`}
                onChange={(_, val) => setForm({ ...form, customer_id: val?.id || 0 })}
                renderInput={(params) => <TextField {...params} label="Cliente" size="small" required />}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Monto prestado"
                type="number"
                fullWidth
                size="small"
                required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Interés (%)"
                type="number"
                fullWidth
                size="small"
                value={form.interest_rate}
                onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Cantidad de cuotas"
                type="number"
                fullWidth
                size="small"
                required
                value={form.installments}
                onChange={(e) => setForm({ ...form, installments: e.target.value })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Día de cobro (1=Lun, 7=Dom)"
                type="number"
                fullWidth
                size="small"
                value={form.collection_day}
                onChange={(e) => setForm({ ...form, collection_day: e.target.value })}
                inputProps={{ min: 1, max: 7 }}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                freeSolo
                size="small"
                options={vendors.map((v) => v.name)}
                value={form.vendor_name}
                onChange={(_, val) => setForm({ ...form, vendor_name: (val as string) || '' })}
                onInputChange={(_, val) => setForm({ ...form, vendor_name: val || '' })}
                renderInput={(params) => <TextField {...params} label="Vendedor" />}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                size="small"
                options={appUsers.filter((u) => u.is_active).map((u) => u.username)}
                value={form.collector_name || null}
                onChange={(_, val) => setForm({ ...form, collector_name: (val as string) || '' })}
                renderInput={(params) => <TextField {...params} label="Cobrador" />}
              />
            </Grid>
            {form.amount && form.installments && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Total con interés: ${totalAmount.toLocaleString()} | Valor cuota: ${installmentPreview.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Alert>
              </Grid>
            )}
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate}>Crear Crédito</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={() => deleteDialog.loading ? null : setDeleteDialog({ open: false, credit: null, loading: false, error: '' })}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ color: 'error.main' }}>Eliminar crédito definitivamente</DialogTitle>
        <DialogContent>
          {deleteDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{deleteDialog.error}</Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            Vas a eliminar definitivamente el crédito de <strong>{deleteDialog.credit?.customer_name}</strong>.
            <br /><br />
            Se borrarán también:
            <ul style={{ margin: '8px 0' }}>
              <li>Todos los pagos registrados</li>
              <li>Reprogramaciones y ajustes</li>
              <li>El gasto asociado al préstamo</li>
            </ul>
            <strong>Esta acción no se puede deshacer.</strong>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Si solo querés marcarlo como impago, usá "Marcar como incobrable" en su lugar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, credit: null, loading: false, error: '' })}
            disabled={deleteDialog.loading}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteDialog.loading}
          >
            {deleteDialog.loading ? 'Eliminando...' : 'Eliminar definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={writeoffDialog.open}
        onClose={() => setWriteoffDialog({ open: false, credit: null, reason: '' })}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Marcar crédito como incobrable</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            El crédito de <strong>{writeoffDialog.credit?.customer_name}</strong> se marcará como incobrable. Dejará de aparecer en cobranzas y pasará al listado de morosos.
          </Alert>
          <TextField
            label="Motivo (opcional)"
            fullWidth
            multiline
            rows={3}
            value={writeoffDialog.reason}
            onChange={(e) => setWriteoffDialog({ ...writeoffDialog, reason: e.target.value })}
            placeholder="Ej: se mudó, no contesta, falleció, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWriteoffDialog({ open: false, credit: null, reason: '' })}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleMarkDefaulted}>Marcar incobrable</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
